/**
 * Semantic director actions. Keyboard (input.ts), mouse clicks on the console
 * UI and the bot director all produce these; `applyDirectorAction` routes them
 * to the mixer.
 */
import type { CommentatorRequest, GraphicKind, MatchEvent, OperatorInstruction, WorldSnapshot } from '../core/contracts';
import type { Mixer, ReplayRange } from './mixer';
import { pickLowerThird } from './lowerThird';

export type DirectorAction =
  | { type: 'hot_punch'; camId: string }
  | { type: 'preview'; camId: string }
  | { type: 'take' }
  | { type: 'mix'; durationS?: number }
  | { type: 'replay_toggle' }                       // R: roll latest replay-worthy, or end if active
  | { type: 'replay_roll'; eventId?: number | null } // explicit event (UI event list / bot)
  | { type: 'replay_angle'; camId: string }
  | { type: 'replay_end' }
  | { type: 'graphic_context' }                     // Q: context-aware lower third
  | { type: 'graphic_show'; kind: GraphicKind; subjectId?: string }
  | { type: 'graphic_toggle'; kind: GraphicKind; subjectId?: string }
  | { type: 'graphic_hide'; target: number | GraphicKind }
  | { type: 'scorebug_toggle' }
  | { type: 'graphics_clear' }
  | { type: 'booth_focus'; dir: 1 | -1 }            // Tab / Shift+Tab
  | { type: 'instruct'; camId: string; instruction: OperatorInstruction };

export interface ActionContext {
  buffer: ReplayRange;
  events: () => readonly MatchEvent[];
  snapshot: () => WorldSnapshot;
  /** Booth focus is owned by the UI / grading layer. */
  onBoothFocus?: (dir: 1 | -1) => void;
  /** Open booth requests (Q answers a player-graphic request first). */
  requests?: () => readonly CommentatorRequest[];
  /** Focused booth request id (Tab / click). */
  boothFocusId?: () => number | null;
}

/** Apply an action to the mixer. Returns true if it changed anything. */
export function applyDirectorAction(mixer: Mixer, a: DirectorAction, ctx: ActionContext): boolean {
  switch (a.type) {
    case 'hot_punch': return mixer.hotPunch(a.camId);
    case 'preview': return mixer.preview(a.camId);
    case 'take': return mixer.take();
    case 'mix': return mixer.mix(a.durationS);
    case 'replay_toggle':
      return mixer.state.replay.active ? mixer.endReplay() : mixer.rollReplay(null, ctx.buffer);
    case 'replay_roll': {
      const ev = a.eventId == null ? null : ctx.events().find((e) => e.id === a.eventId) ?? null;
      return mixer.rollReplay(ev, ctx.buffer);
    }
    case 'replay_angle': return mixer.replayCut(a.camId);
    case 'replay_end': return mixer.endReplay();
    case 'graphic_context': {
      const pick = pickLowerThird(ctx.events(), ctx.snapshot(), undefined, {
        requests: ctx.requests?.() ?? [], focusId: ctx.boothFocusId?.() ?? null, onAir: mixer.state.graphics,
      });
      const subjectless = pick.kind === 'half_time_score' || pick.kind === 'full_time_score';
      if (!pick.subjectId && !subjectless) return false;
      mixer.showGraphic(pick.kind, pick.subjectId ?? undefined);
      return true;
    }
    case 'graphic_show': mixer.showGraphic(a.kind, a.subjectId); return true;
    case 'graphic_toggle': mixer.toggleGraphic(a.kind, a.subjectId); return true;
    case 'graphic_hide': return mixer.hideGraphic(a.target);
    case 'scorebug_toggle': mixer.toggleScorebug(); return true;
    case 'graphics_clear': mixer.clearGraphics(); return true;
    case 'booth_focus': ctx.onBoothFocus?.(a.dir); return !!ctx.onBoothFocus;
    case 'instruct': mixer.setInstruction(a.camId, a.instruction); return true;
  }
}
